import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import TextInput from '@shared/components/forms/inputs/input/input.vue';
import Checkbox from '@shared/components/forms/inputs/checkbox/checkbox.vue';
import { Login } from '@auth/interfaces/login';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { Subscription } from 'rxjs';
import { AuthState } from '@auth/interfaces/auth-state';
import { State, Action, Getter } from 'vuex-class';
import { ResetPassword } from '@auth/interfaces/reset-password';

const namespace: string = 'auth';

@Component({
  name: 'bpc-form-reset-password',
  components: {
    'bpc-input': TextInput,
    'bpc-checkbox': Checkbox,
    ValidationProvider,
    ValidationObserver,
  },
})
export default class FormResetPassword extends Vue {
  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  @Prop({ type: String, default: null }) readonly emailFromBack: string;
  @Prop({ type: String, default: null }) readonly token: string;
  @Prop({ type: String, default: null }) readonly recaptcha: string;

  get lang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  public formModel: ResetPassword = new ResetPassword();

  emailAttributes: object = {
    fieldAttributes: {
      description: "We'll never share your email with anyone else.",
    },
    placeholder: this.$t('global.header.login_widget.recover_password.email_placeholder'),
    rules: ['required'],
    ...FormResetPassword.getCommonAttributes('email'),
    showLabel: false,
  };

  passwordAttributes: object = {
    fieldAttributes: {
      type: 'password',
    },
    placeholder: this.$t('global.header.login_widget.recover_password.password_placeholder'),
    rules: ['required'],
    ...FormResetPassword.getCommonAttributes('password'),
    showLabel: false,
  };

  passwordConfirmationAttributes: object = {
    fieldAttributes: {
      type: 'password',
    },
    placeholder: this.$t(
      'global.header.login_widget.recover_password.password_confirm_placeholder',
    ),
    rules: ['required'],
    ...FormResetPassword.getCommonAttributes('password confirmation'),
    showLabel: false,
  };

  async resetPassword() {
    const isValid = await this.$refs.observer.validate();
    if (isValid) {
      this.$bpcApi
        .post(`${this.lang}/password/reset`, {
          email: this.emailFromBack,
          token: this.token,
          password: this.formModel.password,
          password_confirmation: this.formModel.password_confirmation,
          recaptcha: this.recaptcha,
        })
        .subscribe(
          res => {
            this.$emit('show-alert', { status: 'success', message: res.data.message });
          },
          error => {
            if (error.response.status == '422') {
              this.$emit('show-alert', { status: 'danger', message: error.response.data.message });
              this.$emit('reset-recaptcha');
            }
            if (error.response.status == '400') {
              this.$emit('show-alert', { status: 'danger', message: error.response.data.message });
            }
          },
        );
    } else {
      this.$emit('change-sending-password', false);
    }
  }

  private static getCommonAttributes(name: string): object {
    return {
      id: `${name}`,
      showLabel: true,
      labelClass: 'label-class',
      labelAttributes: {
        for: `${name}`, // Set to the ID of the singular form-control in the form-group.
        // Do not set a value if there is more than one form control in
        // the group.
        cols: 12, // Number of columns for the label width xs screens and up
        'cols-sm': 12, // Number of columns for the label width sm screens and up
        'cols-md': 12, // Number of columns for the label width md screens and up
        'cols-lg': 12, // Number of columns for the label width lg screens and up
        'cols-xl': 12, // Number of columns for the label width xl screens and up
        'align-sm': 'left', //Text alignment 'left', 'center', 'right' for the label sm screens and up
        'align-md': 'left', //Text alignment 'left', 'center', 'right' for the label md screens and up
        'align-lg': 'left', //Text alignment 'left', 'center', 'right' for the label lg screens and up
        'align-xl': 'left', //Text alignment 'left', 'center', 'right' for the label xl screens and up
        'sr-only': false, // to have the label size match the form control size
        align: 'right', // Text alignment 'left', 'center', 'right' for the label xs screens and up
        size: 'md', // Sets the text size of the label: 'sm', 'md' (default) or 'lg'. Use this prop
      },
      name: `${name}`,
      fieldClass: 'input-class',
      state: false, // Controls the validation state of the feedback.
      // 'true' + showError force shows valid-feedback,
      // 'false' + showFeedback force shows invalid feedback,
      // 'null' does not force show the feedback
      veeValidation: true,
      showError: true,
      errorClass: 'error-class',
      errorAttributes: {},
      error: 'error message',
      showHelp: false,
      helpClass: 'help-class',
      helpAttributes: {},
      help: `${name} help`,
      showFeedback: true,
      feedbackClass: 'feedback-class',
      feedbackAttributes: {},
      feedback: 'This is only for feedback',
    };
  }
}
